// Deal Inbox - Full page VC deal management
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Search, Filter, SortAsc, SortDesc, 
  ExternalLink, Mail, FileText, MoreHorizontal,
  ChevronDown, X, MessageSquare, Calendar, Sparkles,
  Building2, Users, TrendingUp, Target, Loader2, Trash2
} from 'lucide-react';
import { getPageSubmissions, updateSubmissionStatus, deleteSubmission, updateSubmissionNotes, getMyPage } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Status configuration with colors and workflow
const STATUS_CONFIG = {
  new: { label: 'New', color: '#3B82F6', bg: '#EFF6FF', order: 1 },
  needs_info: { label: 'Needs Info', color: '#F59E0B', bg: '#FFFBEB', order: 2 },
  ready_review: { label: 'Ready for Review', color: '#8B5CF6', bg: '#F5F3FF', order: 3 },
  meeting: { label: 'Meeting', color: '#10B981', bg: '#ECFDF5', order: 4 },
  pass: { label: 'Pass', color: '#EF4444', bg: '#FEF2F2', order: 5 },
  parked: { label: 'Parked', color: '#6B7280', bg: '#F3F4F6', order: 6 },
};

// Score badge component
function ScoreBadge({ score }) {
  if (!score && score !== 0) return <span style={{ color: '#9CA3AF', fontSize: '13px' }}>—</span>;
  
  const getColor = (s) => {
    if (s >= 70) return { bg: '#DCFCE7', text: '#166534' };
    if (s >= 50) return { bg: '#FEF9C3', text: '#854D0E' };
    if (s >= 30) return { bg: '#FED7AA', text: '#9A3412' };
    return { bg: '#FEE2E2', text: '#991B1B' };
  };
  
  const colors = getColor(score);
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 10px',
      background: colors.bg,
      color: colors.text,
      fontSize: '13px',
      fontWeight: '600',
      borderRadius: '100px'
    }}>
      <Sparkles style={{ width: '12px', height: '12px' }} />
      {score}
    </span>
  );
}

// Status dropdown component
function StatusDropdown({ status, onStatusChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          background: config.bg,
          color: config.color,
          border: 'none',
          borderRadius: '6px',
          fontSize: '13px',
          fontWeight: '500',
          cursor: 'pointer',
          whiteSpace: 'nowrap'
        }}
      >
        {config.label}
        <ChevronDown style={{ width: '14px', height: '14px' }} />
      </button>
      
      {isOpen && (
        <>
          <div 
            style={{ position: 'fixed', inset: 0, zIndex: 40 }} 
            onClick={() => setIsOpen(false)} 
          />
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '4px',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            border: '1px solid #E5E7EB',
            zIndex: 50,
            minWidth: '160px',
            overflow: 'hidden'
          }}>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <button
                key={key}
                onClick={() => {
                  onStatusChange(key);
                  setIsOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '10px 14px',
                  border: 'none',
                  background: status === key ? '#F3F4F6' : 'white',
                  color: '#374151',
                  fontSize: '13px',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: cfg.color
                }} />
                {cfg.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Missing info indicator
function MissingBadge() {
  return (
    <span style={{
      color: '#DC2626',
      fontSize: '12px',
      fontWeight: '500',
      fontStyle: 'italic'
    }}>
      Missing
    </span>
  );
}

// Inline notes editor
function NotesCell({ value, onChange }) {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(value || '');
  
  const handleSave = () => {
    onChange(text);
    setIsEditing(false);
  };
  
  if (isEditing) {
    return (
      <div style={{ display: 'flex', gap: '4px' }}>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          onBlur={handleSave}
          autoFocus
          style={{
            flex: 1,
            padding: '6px 8px',
            border: '1px solid #6366F1',
            borderRadius: '4px',
            fontSize: '13px',
            outline: 'none'
          }}
          placeholder="Add note..."
        />
      </div>
    );
  }
  
  return (
    <div
      onClick={() => setIsEditing(true)}
      style={{
        padding: '6px 8px',
        borderRadius: '4px',
        cursor: 'pointer',
        color: value ? '#374151' : '#9CA3AF',
        fontSize: '13px',
        minHeight: '32px',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {value || 'Click to add note...'}
    </div>
  );
}

// Detail panel that slides in
function DetailPanel({ submission, onClose, onStatusChange, onDelete }) {
  if (!submission) return null;
  
  const data = submission.data || {};
  const sectorInfo = submission.ai_sector_info || {};
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      width: '500px',
      background: 'white',
      boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
      zIndex: 100,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px',
        borderBottom: '1px solid #E5E7EB',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            {data.company_name || 'Unknown Company'}
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6B7280' }}>
            {data.one_liner || 'No description'}
          </p>
        </div>
        <button
          onClick={onClose}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '6px',
            border: 'none',
            background: '#F3F4F6',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <X style={{ width: '16px', height: '16px' }} />
        </button>
      </div>
      
      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
        {/* AI Summary */}
        {submission.ai_executive_summary && (
          <div style={{
            padding: '16px',
            background: 'linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)',
            borderRadius: '12px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <Sparkles style={{ width: '16px', height: '16px', color: '#6366F1' }} />
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#4F46E5' }}>AI Assessment</span>
              <ScoreBadge score={submission.ai_score} />
            </div>
            <p style={{ margin: 0, fontSize: '14px', color: '#1E1B4B', lineHeight: '1.7' }}>
              {submission.ai_executive_summary}
            </p>
          </div>
        )}
        
        {/* All Form Fields in 2x2 Grid */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ 
            fontSize: '13px', 
            fontWeight: '600', 
            color: '#374151', 
            marginBottom: '16px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Form Submission
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {Object.entries(data).map(([key, value]) => {
              // Skip empty values or internal fields
              if (!value || key.startsWith('_')) return null;
              
              // Format the key into a readable label
              const label = key
                .replace(/_/g, ' ')
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, str => str.toUpperCase())
                .trim();
              
              return (
                <div 
                  key={key}
                  style={{ 
                    padding: '12px', 
                    background: '#F9FAFB', 
                    borderRadius: '8px'
                  }}
                >
                  <div style={{ 
                    fontSize: '11px', 
                    color: '#6B7280', 
                    marginBottom: '4px', 
                    textTransform: 'uppercase',
                    fontWeight: '600'
                  }}>
                    {label}
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: '#111827',
                    wordBreak: 'break-word'
                  }}>
                    {typeof value === 'string' && value.startsWith('http') ? (
                      <a 
                        href={value} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#2563EB', textDecoration: 'underline' }}
                      >
                        {value.length > 40 ? value.substring(0, 40) + '...' : value}
                      </a>
                    ) : Array.isArray(value) ? (
                      value.join(', ')
                    ) : (
                      String(value)
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Founders (AI extracted) */}
        {submission.ai_quick_summary?.founders && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              <Users style={{ width: '14px', height: '14px', display: 'inline', marginRight: '6px' }} />
              Founders (AI Extracted)
            </h3>
            <p style={{ margin: 0, fontSize: '14px', color: '#4B5563', lineHeight: '1.6' }}>
              {submission.ai_quick_summary.founders}
            </p>
          </div>
        )}
        
        {/* Problem & Solution */}
        {(sectorInfo.problem || sectorInfo.solution) && (
          <div style={{ marginBottom: '20px' }}>
            {sectorInfo.problem && (
              <div style={{ marginBottom: '12px' }}>
                <h3 style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Problem</h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#4B5563', lineHeight: '1.5' }}>{sectorInfo.problem}</p>
              </div>
            )}
            {sectorInfo.solution && (
              <div>
                <h3 style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Solution</h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#4B5563', lineHeight: '1.5' }}>{sectorInfo.solution}</p>
              </div>
            )}
          </div>
        )}
        
        {/* Next Steps */}
        {submission.ai_next_steps && (
          <div style={{
            padding: '16px',
            background: '#FFFBEB',
            border: '1px solid #F59E0B',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#92400E', marginBottom: '8px', textTransform: 'uppercase' }}>
              ⚡ Recommended Next Steps
            </h3>
            <div style={{ fontSize: '13px', color: '#78350F', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
              {submission.ai_next_steps}
            </div>
          </div>
        )}
        
        {/* Pitch Deck */}
        {(data.deck_url || (submission.files && submission.files.length > 0)) && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              <FileText style={{ width: '14px', height: '14px', display: 'inline', marginRight: '6px' }} />
              Pitch Deck
            </h3>
            <a
              href={data.deck_url || submission.files?.[0]?.url || submission.files?.[0]}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                background: '#EFF6FF',
                color: '#2563EB',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                textDecoration: 'none'
              }}
            >
              <ExternalLink style={{ width: '14px', height: '14px' }} />
              View Deck
            </a>
          </div>
        )}
      </div>
      
      {/* Footer Actions */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid #E5E7EB',
        display: 'flex',
        gap: '12px'
      }}>
        <a
          href={`mailto:${data.email}`}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '12px',
            background: '#4F46E5',
            color: 'white',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            textDecoration: 'none'
          }}
        >
          <Mail style={{ width: '16px', height: '16px' }} />
          Reply via Email
        </a>
        <button
          onClick={() => {
            if (window.confirm('Delete this submission?')) {
              onDelete(submission.id);
              onClose();
            }
          }}
          style={{
            padding: '12px 16px',
            background: '#FEE2E2',
            color: '#DC2626',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer'
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// Main component
export default function DealInboxPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [pageId, setPageId] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('new');
  const [sortBy, setSortBy] = useState('submitted_at');
  const [sortDir, setSortDir] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  
  // Fetch page - prioritize pageId from URL params (for admin mode), then fall back to user's own page
  useEffect(() => {
    async function fetchPage() {
      if (!user) return;
      
      // Check if pageId is passed in URL (from admin editing another user's profile)
      const urlPageId = searchParams.get('pageId');
      if (urlPageId) {
        setPageId(urlPageId);
        return;
      }
      
      // Fall back to user's own page
      try {
        const page = await getMyPage();
        if (page?.id) {
          setPageId(page.id);
        }
      } catch (err) {
        console.error('Failed to fetch page:', err);
        toast.error('Failed to load your page');
      }
    }
    fetchPage();
  }, [user, searchParams]);
  
  const loadSubmissions = useCallback(async () => {
    if (!pageId) return;
    try {
      setLoading(true);
      const data = await getPageSubmissions(pageId);
      setSubmissions(data.submissions || []);
    } catch (err) {
      console.error('Failed to load submissions:', err);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [pageId]);
  
  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);
  
  const handleStatusChange = async (submissionId, newStatus) => {
    try {
      await updateSubmissionStatus(submissionId, newStatus);
      setSubmissions(prev => prev.map(s => 
        s.id === submissionId ? { ...s, status: newStatus } : s
      ));
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };
  
  const handleDelete = async (submissionId) => {
    try {
      await deleteSubmission(submissionId);
      setSubmissions(prev => prev.filter(s => s.id !== submissionId));
      toast.success('Submission deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };
  
  const handleNotesChange = async (submissionId, notes) => {
    // Update locally first for instant feedback
    setSubmissions(prev => prev.map(s => 
      s.id === submissionId ? { ...s, notes } : s
    ));
    
    // Persist to backend
    try {
      await updateSubmissionNotes(submissionId, notes);
    } catch (err) {
      console.error('Failed to save notes:', err);
      toast.error('Failed to save notes');
    }
  };
  
  // Filter and sort submissions
  const filteredSubmissions = submissions
    .filter(s => {
      if (filter !== 'all' && s.status !== filter) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const companyName = s.data?.company_name?.toLowerCase() || '';
        const email = s.data?.email?.toLowerCase() || '';
        return companyName.includes(query) || email.includes(query);
      }
      return true;
    })
    .sort((a, b) => {
      let aVal, bVal;
      if (sortBy === 'submitted_at') {
        aVal = new Date(a.submitted_at || 0);
        bVal = new Date(b.submitted_at || 0);
      } else if (sortBy === 'ai_score') {
        aVal = a.ai_score || 0;
        bVal = b.ai_score || 0;
      } else if (sortBy === 'company') {
        aVal = a.data?.company_name || '';
        bVal = b.data?.company_name || '';
      } else if (sortBy === 'status') {
        aVal = STATUS_CONFIG[a.status]?.order || 0;
        bVal = STATUS_CONFIG[b.status]?.order || 0;
      }
      if (sortDir === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  const counts = {
    all: submissions.length,
    new: submissions.filter(s => s.status === 'new').length,
    needs_info: submissions.filter(s => s.status === 'needs_info').length,
    ready_review: submissions.filter(s => s.status === 'ready_review').length,
    meeting: submissions.filter(s => s.status === 'meeting').length,
    pass: submissions.filter(s => s.status === 'pass').length,
    parked: submissions.filter(s => s.status === 'parked').length,
  };

  // Auth check
  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F9FAFB' }}>
        <Loader2 style={{ width: '32px', height: '32px', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }
  
  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#F9FAFB',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #E5E7EB',
        padding: '16px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => {
              const urlPageId = searchParams.get('pageId');
              // If we came from admin mode (pageId in URL), go back to admin editor
              if (urlPageId) {
                navigate(`/admin/edit/${urlPageId}`);
              } else {
                navigate('/editor');
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              background: '#F3F4F6',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#374151'
            }}
          >
            <ArrowLeft style={{ width: '16px', height: '16px' }} />
            Back to Editor
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Deal Inbox</h1>
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6B7280' }}>
              {submissions.length} total submissions
            </p>
          </div>
        </div>
        
        {/* Search */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          background: '#F3F4F6',
          borderRadius: '8px',
          width: '300px'
        }}>
          <Search style={{ width: '16px', height: '16px', color: '#9CA3AF' }} />
          <input
            type="text"
            placeholder="Search companies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              outline: 'none',
              fontSize: '14px'
            }}
          />
        </div>
      </div>
      
      {/* Filter Pills */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #E5E7EB',
        padding: '12px 32px',
        display: 'flex',
        gap: '8px',
        overflowX: 'auto'
      }}>
        <button
          onClick={() => setFilter('all')}
          style={{
            padding: '8px 16px',
            borderRadius: '100px',
            border: 'none',
            background: filter === 'all' ? '#1F2937' : '#F3F4F6',
            color: filter === 'all' ? 'white' : '#4B5563',
            fontSize: '13px',
            fontWeight: '500',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          All ({counts.all})
        </button>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            style={{
              padding: '8px 16px',
              borderRadius: '100px',
              border: 'none',
              background: filter === key ? cfg.color : cfg.bg,
              color: filter === key ? 'white' : cfg.color,
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {cfg.label} ({counts[key] || 0})
          </button>
        ))}
      </div>
      
      {/* Table */}
      <div style={{ flex: 1, padding: '24px 32px', overflow: 'auto', paddingBottom: '200px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#6B7280' }}>
            Loading submissions...
          </div>
        ) : filteredSubmissions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#6B7280' }}>
            No submissions found
          </div>
        ) : (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #E5E7EB'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>
                    <button 
                      onClick={() => { setSortBy('status'); setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', color: '#6B7280', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase' }}
                    >
                      Status
                      {sortBy === 'status' && (sortDir === 'asc' ? <SortAsc style={{ width: '12px' }} /> : <SortDesc style={{ width: '12px' }} />)}
                    </button>
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>
                    <button 
                      onClick={() => { setSortBy('company'); setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', color: '#6B7280', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase' }}
                    >
                      Company
                      {sortBy === 'company' && (sortDir === 'asc' ? <SortAsc style={{ width: '12px' }} /> : <SortDesc style={{ width: '12px' }} />)}
                    </button>
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Founders</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Sector</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Stage</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>
                    <button 
                      onClick={() => { setSortBy('ai_score'); setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', color: '#6B7280', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase' }}
                    >
                      Score
                      {sortBy === 'ai_score' && (sortDir === 'asc' ? <SortAsc style={{ width: '12px' }} /> : <SortDesc style={{ width: '12px' }} />)}
                    </button>
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>
                    <button 
                      onClick={() => { setSortBy('submitted_at'); setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', color: '#6B7280', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase' }}
                    >
                      Date
                      {sortBy === 'submitted_at' && (sortDir === 'asc' ? <SortAsc style={{ width: '12px' }} /> : <SortDesc style={{ width: '12px' }} />)}
                    </button>
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', minWidth: '200px' }}>Notes</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Files</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', width: '50px' }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredSubmissions.map((submission, idx) => {
                  const data = submission.data || {};
                  const sectorInfo = submission.ai_sector_info || {};
                  const founders = submission.ai_quick_summary?.founders;
                  const founderShort = founders 
                    ? (founders.length > 50 ? founders.substring(0, 50) + '...' : founders)
                    : null;
                  
                  return (
                    <tr 
                      key={submission.id}
                      style={{ 
                        borderBottom: idx < filteredSubmissions.length - 1 ? '1px solid #E5E7EB' : 'none',
                        cursor: 'pointer',
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                      <td style={{ padding: '16px', verticalAlign: 'top' }}>
                        <StatusDropdown 
                          status={submission.status || 'new'} 
                          onStatusChange={(newStatus) => handleStatusChange(submission.id, newStatus)}
                        />
                      </td>
                      <td 
                        style={{ padding: '16px', verticalAlign: 'top' }}
                        onClick={() => setSelectedSubmission(submission)}
                      >
                        <div style={{ fontWeight: '500', color: '#111827' }}>
                          {data.company_name || 'Unknown'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                          {data.one_liner ? (data.one_liner.length > 40 ? data.one_liner.substring(0, 40) + '...' : data.one_liner) : <MissingBadge />}
                        </div>
                      </td>
                      <td 
                        style={{ padding: '16px', fontSize: '13px', color: '#4B5563', verticalAlign: 'top', maxWidth: '200px' }}
                        onClick={() => setSelectedSubmission(submission)}
                      >
                        {founderShort || <MissingBadge />}
                      </td>
                      <td 
                        style={{ padding: '16px', verticalAlign: 'top' }}
                        onClick={() => setSelectedSubmission(submission)}
                      >
                        {(sectorInfo.sector || data.sector) ? (
                          <span style={{
                            padding: '4px 10px',
                            background: '#EEF2FF',
                            color: '#4338CA',
                            fontSize: '12px',
                            fontWeight: '500',
                            borderRadius: '100px'
                          }}>
                            {sectorInfo.sector || data.sector}
                          </span>
                        ) : <MissingBadge />}
                      </td>
                      <td 
                        style={{ padding: '16px', fontSize: '13px', verticalAlign: 'top' }}
                        onClick={() => setSelectedSubmission(submission)}
                      >
                        {data.stage || <MissingBadge />}
                      </td>
                      <td 
                        style={{ padding: '16px', verticalAlign: 'top' }}
                        onClick={() => setSelectedSubmission(submission)}
                      >
                        <ScoreBadge score={submission.ai_score} />
                      </td>
                      <td 
                        style={{ padding: '16px', fontSize: '13px', color: '#6B7280', verticalAlign: 'top' }}
                        onClick={() => setSelectedSubmission(submission)}
                      >
                        {formatDate(submission.submitted_at)}
                      </td>
                      <td style={{ padding: '16px', verticalAlign: 'top' }}>
                        <NotesCell 
                          value={submission.notes} 
                          onChange={(notes) => handleNotesChange(submission.id, notes)}
                        />
                      </td>
                      <td 
                        style={{ padding: '16px', verticalAlign: 'top' }}
                        onClick={() => setSelectedSubmission(submission)}
                      >
                        {(data.deck_url || (submission.files && submission.files.length > 0)) ? (
                          <a
                            href={data.deck_url || submission.files?.[0]?.url || submission.files?.[0]}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '6px 10px',
                              background: '#EFF6FF',
                              color: '#2563EB',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '500',
                              textDecoration: 'none'
                            }}
                          >
                            <FileText style={{ width: '12px', height: '12px' }} />
                            PDF
                          </a>
                        ) : (
                          <span style={{ color: '#9CA3AF', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '16px', verticalAlign: 'top' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this submission?')) {
                              handleDelete(submission.id);
                            }
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '4px',
                            color: '#9CA3AF',
                            transition: 'all 0.15s'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#DC2626'; e.currentTarget.style.background = '#FEE2E2'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = '#9CA3AF'; e.currentTarget.style.background = 'none'; }}
                          title="Delete submission"
                        >
                          <Trash2 style={{ width: '16px', height: '16px' }} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Detail Panel */}
      {selectedSubmission && (
        <>
          <div 
            style={{ 
              position: 'fixed', 
              inset: 0, 
              background: 'rgba(0,0,0,0.3)', 
              zIndex: 90 
            }} 
            onClick={() => setSelectedSubmission(null)} 
          />
          <DetailPanel
            submission={selectedSubmission}
            onClose={() => setSelectedSubmission(null)}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
          />
        </>
      )}
    </div>
  );
}
